import {
	headerCheckboxChange,
	resetConfigurationsStore,
	rowCheckboxChange,
} from '@Actions';
import {
	useConfigurationsSearch,
	useSensorsHealthStatus,
	useSensorsLatestUptimeData,
	useSensorsRedirects,
} from '@Hooks';
import {
	Button,
	EmptyTableBody,
	SensorIllustration,
	StylovyzeTableBody,
	StylovyzeTableHeaderProps,
	StylovyzeTableRow,
	useIsFeatureEnabled,
	TableCell,
	useCompanyDateTime,
	useSelectFeatures,
} from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { FLAGS } from '@Types';
import { ConfigurationRow } from '@Types/configurations.types';
import { isVirtualSensor } from '@Utils';
import { addRouteNamespace } from '@Utils/actions';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigurationStatusTag } from './ConfigurationStatusTag';
import TableCellWithLoading from './TableCellWithLoading';

const SensorConfigurationsTable = () => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const [redirectToCreateSensor] = useSensorsRedirects();
	const {
		configurations: { list, headerCheckboxChecked },
	} = useSelector(getValue);
	const { formatDate } = useCompanyDateTime();
	const features = useSelectFeatures();

	const isLastValueEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_LAST_VALUE_FOR_SENSOR_CONFIGURATION,
	);
	const isLastReadDateTimeEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_LAST_READ_DATE_TIME_SENSOR_CONFIGURATION,
	);
	const isStatusColumnEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_HEALTH_STATUS_FOR_SENSOR_CONFIGURATION,
	);
	const isSensorHealthV3ApiEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_SENSOR_HEALTH_V3_API,
	);
	const unloadLagMinutes = features[FLAGS.SENSOR_HEALTH_UNLOAD_LAG_MINUTES];

	const headers: StylovyzeTableHeaderProps[] = useMemo(() => {
		const columns: StylovyzeTableHeaderProps[] = [
			{
				key: 'sensorId',
				cell: t('Sensor ID'),
				align: 'left',
				sortable: false,
			},
			{
				key: 'sensorAlias',
				cell: t('Alias'),
				align: 'left',
				sortable: false,
			},
			{
				key: 'sensorType',
				cell: t('Type'),
				align: 'left',
				sortable: false,
			},
			{
				key: 'unit',
				cell: t('Unit'),
				align: 'left',
				sortable: false,
			},
		];
		if (isLastValueEnabled) {
			columns.push({
				key: 'lastValue',
				cell: t('Last Value'),
				align: 'left',
				sortable: false,
			});
		}
		if (isLastReadDateTimeEnabled) {
			columns.push({
				key: 'lastValueTimestamp',
				cell: t('Last Reading Date/Time'),
				align: 'left',
				sortable: false,
			});
		}
		if (isStatusColumnEnabled) {
			columns.push({
				key: 'status',
				cell: t('Status'),
				align: 'left',
				sortable: false,
			});
		}
		return columns;
	}, [
		t,
		isLastValueEnabled,
		isStatusColumnEnabled,
		isLastReadDateTimeEnabled,
	]);

	const { initialized } = useConfigurationsSearch();

	const shoulFetchHealthStatus =
		isStatusColumnEnabled ||
		(isLastValueEnabled && !isLastReadDateTimeEnabled);
	const { isSensorsHealthStatusLoading, sensorsHealthStatus } =
		useSensorsHealthStatus({
			shoulFetchHealthStatus,
			isSensorHealthV3ApiEnabled,
			unloadLagMinutes,
		});
	const { isLatestUptimeDataLoading, sensorsLatestUptimeData } =
		useSensorsLatestUptimeData();

	const rows = useMemo(() => {
		return list.map(
			(configuration: ConfigurationRow): StylovyzeTableRow => {
				const sensorHealthData =
					sensorsHealthStatus[configuration.sensorId];
				const sensorUptimeData =
					sensorsLatestUptimeData[configuration.sensorId];
				const cells: TableCell[] = [
					configuration.sensorId,
					configuration.sensorAlias,
					configuration.sensorType,
					configuration.unit,
				];
				if (isLastValueEnabled) {
					const latestValue = isLastReadDateTimeEnabled
						? sensorUptimeData?.latestValue
						: sensorHealthData?.latestValue;
					const latestValueLoading = isLastReadDateTimeEnabled
						? isLatestUptimeDataLoading
						: isSensorsHealthStatusLoading;
					cells.push(
						<TableCellWithLoading isLoading={latestValueLoading}>
							<span>{latestValue}</span>
						</TableCellWithLoading>,
					);
				}
				if (isLastReadDateTimeEnabled) {
					const parsedDateTime =
						sensorUptimeData?.lastValueTimestamp &&
						formatDate({
							date: sensorUptimeData.lastValueTimestamp,
							format: 'date-time',
							dateTimeSeparator: ' ',
						});
					cells.push(
						<TableCellWithLoading
							isLoading={isLatestUptimeDataLoading}>
							<span>{parsedDateTime}</span>
						</TableCellWithLoading>,
					);
				}
				if (isStatusColumnEnabled) {
					cells.push(
						<TableCellWithLoading
							isLoading={isSensorsHealthStatusLoading}>
							<ConfigurationStatusTag
								status={sensorHealthData?.status}
							/>
						</TableCellWithLoading>,
					);
				}
				return {
					selected: configuration.selected,
					key: configuration.sensorId,
					cells: cells,
					pathToGoWhenClicked: addRouteNamespace(
						`sensors/${
							isVirtualSensor(
								configuration.dataSourceType ??
									configuration.sourceType,
							)
								? configuration._id
								: configuration.sensorId
						}/edit`,
					),
					onClick: () => dispatch(resetConfigurationsStore()),
				};
			},
		);
	}, [
		list,
		sensorsHealthStatus,
		sensorsLatestUptimeData,
		isLastValueEnabled,
		isStatusColumnEnabled,
		isLastReadDateTimeEnabled,
	]);
	const handleRowClick = (_?: string, onClick?: () => void) => () => {
		if (onClick) onClick();
	};
	const isCheckboxSelectorEnabled = !!useIsFeatureEnabled(
		'info360-table-selector-and-types-filter',
	);

	return (
		<StylovyzeTableBody
			dataCy="sensor configurations grid"
			headers={headers}
			rows={rows}
			handleRowClick={handleRowClick}
			initialized={initialized}
			checkboxSelector={
				isCheckboxSelectorEnabled
					? {
							headerCheckboxChecked,
							onHeaderCheckboxChange: checked => {
								dispatch(headerCheckboxChange(checked));
							},
							onRowCheckboxChange: (row, checked) => {
								dispatch(rowCheckboxChange({ row, checked }));
							},
					  }
					: undefined
			}
			emptyTableBody={
				<EmptyTableBody
					dataCy="sensor-configuration-empty-table"
					title={t('There are no Sensors to display.')}
					subtitle={t(
						'Sensors configured to display time series data will be displayed here.',
					)}
					icon={<SensorIllustration />}
					color="#4A6067"
					CTA={
						<Button
							variant="contained"
							color="primary"
							onClick={() => {
								redirectToCreateSensor();
							}}>
							{t('Create Sensor')}
						</Button>
					}
				/>
			}
		/>
	);
};

export default SensorConfigurationsTable;
